import React from 'react';
import PropTypes from 'prop-types';

import ERRORS from './errors';
import TABS from './tabs';
import useCourseLogic from './useCourseLogic';

const CourseContext = React.createContext();

function CourseProvider({ children, courseId, navigate }) {
  const {
    canPublish,
    clearError,
    clearMoodleCourseUrl,
    completionIntegrityFlagEnabled,
    course,
    currentTabValidation,
    deleteCourse,
    deletePublishedCourse,
    deleteQuiz,
    detailedErrors,
    enabledLanguages,
    errorMessage,
    errorType,
    fetchLessonsProcessingStatus,
    getFormattedTranslations,
    validateGameQuery,
    validateGameResponse,
    getLanguageStatus,
    getLanguages,
    handleSave,
    hasChanged,
    hasDeleteError,
    hasError,
    hasQuiz,
    loading,
    locked,
    publish,
    publishDraftEpisodes,
    saveAccess,
    saveAttachments,
    saveDescription,
    saveDisplay,
    saveExam,
    saveLessons,
    selectedTab,
    setCurrentTabValidation,
    setHandleSave,
    setHasChanged,
    setHasError,
    setLocked,
    setSelectedTab,
    setSubmitting,
    setUploadingFiles,
    submitting,
    submittingRefresh,
    syncMoodleCourse,
    uploadingFiles,
    validateCourse,
    validationDetails,
  } = useCourseLogic({
    courseId,
    navigate,
  });

  const contextValue = {
    canPublish,
    clearError,
    clearMoodleCourseUrl,
    completionIntegrityFlagEnabled,
    course,
    currentTabValidation,
    deleteCourse,
    deletePublishedCourse,
    deleteQuiz,
    detailedErrors,
    enabledLanguages,
    errorMessage,
    errorType,
    fetchLessonsProcessingStatus,
    getFormattedTranslations,
    validateGameQuery,
    validateGameResponse,
    getLanguageStatus,
    getLanguages,
    handleSave,
    hasChanged,
    hasDeleteError,
    hasError,
    hasQuiz,
    loading,
    locked,
    publish,
    publishDraftEpisodes,
    saveAccess,
    saveAttachments,
    saveDescription,
    saveDisplay,
    saveExam,
    saveLessons,
    selectedTab,
    setCurrentTabValidation,
    setHandleSave,
    setHasChanged,
    setHasError,
    setLocked,
    setSelectedTab,
    setSubmitting,
    setUploadingFiles,
    submitting,
    submittingRefresh,
    syncMoodleCourse,
    uploadingFiles,
    validateCourse,
    validationDetails,
  };

  return (
    <CourseContext.Provider value={contextValue}>
      {children}
    </CourseContext.Provider>
  );
}

CourseProvider.propTypes = {
  children: PropTypes.node.isRequired,
  courseId: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
};

function useCourse() {
  const context = React.useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
}

export { CourseProvider, ERRORS, TABS, useCourse };
